import { cloneDeep } from '@apollo/client/utilities';
import {
    Badge,
    Button,
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Separator,
} from 'app-template-lib/components/shadcn';
import { CirclePlus, Square, CircleX, SquareCheckBig } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

type Props<TData, TIdKey extends keyof TData, TValueKey extends keyof TData, TCountKey extends keyof TData> = {
    items: TData[]; // マルチセレクトの選択肢
    idKey: TIdKey; // itemsの中のIDのキー
    countKey?: TCountKey; // itemsの中のカウントのキー
    valueKey: TValueKey; // itemsの中の表示する値のキー
    name: string; // マルチセレクトの名前
    selectedItems?: TData[]; // 選択されているアイテム
    onSelect?: (selectedItems: TData[]) => void; // 選択されたアイテムが変更されたときのコールバック
    maxViewCount?: number; // 表示するアイテムの最大数
    className?: string;
    visibleItemCount?: boolean;
};

export default function MultiSelect<TData, TIdKey extends keyof TData, TValueKey extends keyof TData, TCountKey extends keyof TData>({
    items,
    idKey,
    countKey,
    valueKey,
    name,
    selectedItems,
    onSelect,
    maxViewCount = 2,
    className = '',
    visibleItemCount = true,
}: Props<TData, TIdKey, TValueKey, TCountKey>) {
    const [selectedList, setSelectedList] = useState<TData[]>([]);

    // 制御コンポーネントとして使用するため、親コンポーネントからselectedItemsが変更された場合にselectedListを更新する
    useEffect(() => {
        if (selectedItems) {
            setSelectedList(selectedItems);
        }
    }, [selectedItems]);

    const handleSelectItem = useCallback(
        (value: string) => {
            // 選択したアイテムを取得
            let tmpSelectedList = cloneDeep(selectedList);
            const selectedItem = items.find((item) => String(item[idKey]) === value);
            if (selectedItem) {
                // 選択したかどうかを判定
                const isSelected = tmpSelectedList.some((item) => item[idKey] === selectedItem[idKey]);
                if (isSelected) {
                    tmpSelectedList = tmpSelectedList.filter((item) => item[idKey] !== selectedItem[idKey]);
                } else {
                    tmpSelectedList = [...tmpSelectedList, selectedItem];
                }
                setSelectedList(tmpSelectedList);
                // 制御コンポーネント時は、親コンポーネントに選択されたアイテムを返す
                if (onSelect) {
                    onSelect(tmpSelectedList);
                }
            }
        },
        [setSelectedList, selectedList, items, idKey, onSelect]
    );

    /**
     * リセットボタンをクリックしたときのイベントハンドラ
     */
    const handleClickReset = useCallback(() => {
        setSelectedList([]);
        if (onSelect) {
            onSelect([]);
        }
    }, [setSelectedList, onSelect]);

    function ListCountBadge({ selectedList }: { selectedList: TData[] }) {
        if (selectedList.length === 0) return null;
        // 選択されているアイテムのうち、最初のmaxViewCount件を表示する
        const firstItems = selectedList.slice(0, maxViewCount);
        return (
            <>
                <Separator orientation="vertical" className="mx-1" />
                <div className="inline-flex">
                    {firstItems.map((item) => {
                        return (
                            <Badge key={String(item[idKey])} variant="secondary" className="hidden md:inline-block mr-1">
                                {String(item[valueKey])}
                            </Badge>
                        );
                    })}
                    {selectedList.length > maxViewCount && (
                        <Badge variant="secondary" className="hidden md:inline-block mr-1">
                            ...他{selectedList.length - maxViewCount}件
                        </Badge>
                    )}
                    <Badge variant="secondary" className="inline-block md:hidden mr-1">
                        {selectedList.length}件
                    </Badge>
                </div>
                {selectedList.length > 0 && (
                    <Button onClick={handleClickReset} variant={'ghost'} className="bg-card px-1">
                        <CircleX />
                    </Button>
                )}
            </>
        );
    }

    return (
        <div className={`bg-card border-card inline-flex items-center  rounded-lg p-1 ${className}`}>
            <Popover>
                <PopoverTrigger asChild>
                    <Button className="bg-card px-2 max-w-full" variant="ghost">
                        <CirclePlus />
                        {name}
                    </Button>
                </PopoverTrigger>
                <PopoverContent align="start">
                    <Command
                        filter={(_value, search, keywords) => {
                            if (keywords && keywords.some((keyword) => keyword.includes(search))) return 1;
                            return 0;
                        }}
                    >
                        <CommandInput placeholder={name} />
                        <CommandList>
                            <CommandEmpty>データがありません</CommandEmpty>
                            <CommandGroup>
                                {items.map((item) => {
                                    // 選択されているかどうか
                                    const isSelected = selectedList.some((selectedItem) => selectedItem[idKey] === item[idKey]);
                                    return (
                                        <CommandItem
                                            key={String(item[idKey])}
                                            onSelect={handleSelectItem}
                                            value={String(item[idKey])}
                                            keywords={[String(item[valueKey])]}
                                        >
                                            <div className="inline-flex justify-between w-full items-center">
                                                <div className="inline-flex items-center mr-2">
                                                    <div className="mr-1">{isSelected ? <SquareCheckBig /> : <Square />}</div>
                                                    {String(item[valueKey])}
                                                </div>
                                                {visibleItemCount && (
                                                    <div className="text-nowrap">{countKey && `${String(item[countKey])}件`}</div>
                                                )}
                                            </div>
                                        </CommandItem>
                                    );
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            <ListCountBadge selectedList={selectedList} />
        </div>
    );
}
